import React, { ReactNode } from 'react'
import { Logo } from '../components/simple/logo'
import { Button } from '../components/simple/button'
import { SpecialPanel } from '../layouts/components/panel'
import { makeRequest } from '../request'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  showMore: boolean
}

type SerializableErrorInfo = {
  componentStack?: string
  digest?: string
}

type SerializableErrorPayload = {
  error: string
  stack?: string
  errorInfo: SerializableErrorInfo
}

const toSerializableErrorInfo = (errorInfo: React.ErrorInfo): SerializableErrorInfo => {
  // Keep only plain strings so frontend error reporting never trips over circular references.
  const safeInfo: SerializableErrorInfo = {}

  if (typeof errorInfo?.componentStack === 'string') {
    safeInfo.componentStack = errorInfo.componentStack
  }

  const digest = (errorInfo as React.ErrorInfo & { digest?: unknown }).digest
  if (typeof digest === 'string') {
    safeInfo.digest = digest
  }

  return safeInfo
}

const toSerializableErrorPayload = (error: Error, errorInfo: React.ErrorInfo): SerializableErrorPayload => {
  const payload: SerializableErrorPayload = {
    error: String(error),
    stack: typeof error.stack === 'string' ? error.stack : undefined,
    errorInfo: toSerializableErrorInfo(errorInfo),
  }

  try {
    JSON.stringify(payload)
    return payload
  } catch (_e) {
    // Last-resort fallback if a circular value slips into the payload.
    return {
      error: String(error),
      stack: undefined,
      errorInfo: {},
    }
  }
}

export default class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, showMore: false }
  }

  static isChunkLoadError(error: Error): boolean {
    return (
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      error.name === 'ChunkLoadError'
    )
  }

  static isLikelyExternalDomInterferenceError(error: Error): boolean {
    const message = error.message || ''
    return (
      message.includes('Converting circular structure to JSON') &&
      (message.includes('HTMLAnchorElement') || message.includes('__reactFiber') || message.includes('stateNode'))
    )
  }

  static getDerivedStateFromError(error: Error) {
    // Auto-reload for stale chunk errors caused by new deployments
    if (ErrorBoundary.isChunkLoadError(error)) {
      window.location.reload()
      return { hasError: false, error: null, showMore: false }
    }
    // Update state to show fallback UI
    return { hasError: true, error, showMore: false }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error information if needed
    console.error('Error caught in ErrorBoundary:', error, errorInfo)

    // Don't report chunk load errors — they are expected after deployments and trigger a reload
    if (ErrorBoundary.isChunkLoadError(error)) {
      return
    }

    // Browser extensions or injected scripts can attach circular React/DOM refs and trigger this.
    // Skip backend reporting for these non-actionable client environment errors.
    if (ErrorBoundary.isLikelyExternalDomInterferenceError(error)) {
      return
    }

    const payload = toSerializableErrorPayload(error, errorInfo)

    // Report the error to a route
    makeRequest('/frontend_api/reportFrontendError', {
      error: payload.error,
      stack: payload.stack,
      errorInfo: payload.errorInfo,
    }).catch((reportError) => {
      console.error('Error reporting to the server:', reportError)
      // try again to the session free version
      makeRequest('/api/reportFrontendError', {
        error: payload.error,
        stack: payload.stack,
        errorInfo: payload.errorInfo,
      }).catch((reportError) => {
        console.error('Error reporting to the server fallback:', reportError)
      })
    })
  }

  render() {
    if (this.state.hasError) {
      const isLikelyExternalInterference =
        !!this.state.error &&
        (this.state.error.message.includes(
          "Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node."
        ) ||
          ErrorBoundary.isLikelyExternalDomInterferenceError(this.state.error))

      // Render fallback UI
      return (
        <SpecialPanel>
          <Logo />
          {isLikelyExternalInterference ? (
            <>
              <b>Oops! Something went wrong due to external modifications to the page.</b>
              <div>
                Please disable any browser extensions that may be modifying the page and try again. We are sorry for any
                inconvenience.
              </div>
            </>
          ) : (
            <>
              <b>Oops! Something went wrong.</b>
              <div>We are sorry for any inconvenience. Please try again soon. This error has been reported.</div>
            </>
          )}
          <Button logo onClick={() => window.location.reload()}>
            Reload
          </Button>

          {this.state.showMore ? (
            <p className="mt-8 text-sm">
              <b>{this.state.error?.message}</b>
              <br />
              {this.state.error?.stack}
            </p>
          ) : (
            <Button
              small
              onClick={() => {
                this.setState({ showMore: true })
              }}
            >
              Show more
            </Button>
          )}
        </SpecialPanel>
      )
    }

    return this.props.children
  }
}
